'use client';

import { useCallback, useEffect, useState } from 'react';

import { useCatalogPage } from './use-catalog-page';
import { getFirestoreClient } from '@/lib/firebase/client';
import { getBlogPostBySlug, listPublicBlogPosts } from '@/lib/platform/blog';
import type { PlatformBlogPostDoc } from '@/types/platform';

export function usePublicBlogPosts(initialPosts: PlatformBlogPostDoc[] = []) {
  const [posts, setPosts] = useState<PlatformBlogPostDoc[]>(initialPosts);
  const [loading, setLoading] = useState(!initialPosts.length);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setPosts([]);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await listPublicBlogPosts(db);
      setPosts(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load blog posts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialPosts.length) return undefined;
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [initialPosts.length, refresh]);
  return { posts, loading, error, refresh };
}

export function usePublicBlogPost(slug: string, initialPost: PlatformBlogPostDoc | null = null) {
  const [post, setPost] = useState<PlatformBlogPostDoc | null>(initialPost);
  const [loading, setLoading] = useState(!initialPost);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setPost(null);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await getBlogPostBySlug(db, slug);
      setPost(data && data.isPublic && data.publicationStatus === 'published' ? data : null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load blog post.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (initialPost?.slug === slug) return undefined;
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [initialPost, refresh, slug]);
  return { post, loading, error, refresh };
}

export function useAdminBlogPosts(pageSize = 100) {
  const { records: posts, ...page } = useCatalogPage<PlatformBlogPostDoc>(`/api/admin/blog?limit=${pageSize}`, 'posts', 'postId');
  return { posts, ...page };
}
